import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { api } from "@/lib/api";
import { CONTACT } from "@/data/site";
import { useAuth } from "@/lib/auth";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return undefined;
    }
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data?.payment_status === "paid") {
          refresh();
          return setState("paid");
        }
        if (data?.status === "expired") return setState("error");
      } catch {
        /* retry */
      }
      tries += 1;
      if (tries >= 10) return alive && setState("pending");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [sessionId, refresh]);

  return (
    <main className="min-h-screen grid place-items-center px-5 relative overflow-hidden" data-testid="payment-success-page">
      <div className="absolute inset-0 grid-floor opacity-60" />
      <div className="absolute h-[500px] w-[500px] rounded-full bg-[#3b4049]/25 blur-[140px]" />
      <div className="relative glass rounded-[28px] p-8 sm:p-12 max-w-lg w-full text-center">
        <LogoMark className="h-12 w-12 mx-auto mb-8" />
        {state === "checking" && (
          <div data-testid="payment-status-checking">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-[#d5d9df]" />
            <h1 className="font-display text-2xl font-bold mt-6">Confirming your payment…</h1>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-status-paid">
            <CheckCircle2 className="h-14 w-14 mx-auto text-[#d5d9df]" />
            <h1 className="font-display text-3xl font-bold mt-6">You're all set.</h1>
            <p className="text-slate-300 mt-4">Payment confirmed and your credits are ready. Head to the Studio and start swapping.</p>
            <Link to="/#order" data-testid="payment-go-studio" className="btn-primary mt-8 px-7 py-3.5">Open the Studio</Link>
          </div>
        )}
        {(state === "pending" || state === "error") && (
          <div data-testid="payment-status-issue">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-300" />
            <h1 className="font-display text-2xl font-bold mt-6">{state === "pending" ? "Still processing" : "We couldn't confirm this payment"}</h1>
            <p className="text-slate-300 mt-4">Questions? Call {CONTACT.phone} or email {CONTACT.email}.</p>
          </div>
        )}
        <Link to="/" data-testid="payment-back-home" className="btn-ghost mt-4 px-6 py-3 text-sm">Back to Frame Shift</Link>
      </div>
    </main>
  );
}
